import { httpRequest } from "@/lib";
import { useQuery, useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

export type PredictiveNoShow = {
    patient_name: string;
    date: string;
    procedure: string;
    risk_score: number;
    risk_factors: string[];
    recommended_action: string;
};

export type NoShowPredictionResponse = {
    tenant_id: string;
    predictions: PredictiveNoShow[];
};

export type SwotMatrix = {
    strengths: string[];
    weaknesses: string[];
    opportunities: string[];
    threats: string[];
}

export type SwotWizardResponse = {
    tenant_id: string;
    swot_matrix: SwotMatrix;
    ai_message: string;
}

export type PredictiveForecastResponse = {
    tenant_id: string;
    current_month_baseline: number;
    forecasts: {
        "30_days": {
            projected_revenue: number;
            confidence_interval?: number;
            growth_trend: string;
        };
        "60_days": {
            projected_revenue: number;
            confidence_interval: number;
            growth_trend: string;
        };
        "90_days": {
            projected_revenue: number;
            confidence_interval: number;
            growth_trend: string;
        };
    };
    insights: string[];
};

export function usePredictiveDashboard() {
    const noShowsQuery = useQuery({
        queryKey: ["predictive-no-shows"],
        queryFn: async (): Promise<AxiosResponse<NoShowPredictionResponse>> => {
            return await httpRequest.get(`/dashboard/predictive/no-shows`);
        },
        keepPreviousData: true,
    });

    const forecastQuery = useQuery({
        queryKey: ["predictive-forecast"],
        queryFn: async (): Promise<AxiosResponse<PredictiveForecastResponse>> => {
            return await httpRequest.get(`/dashboard/predictive/forecast`);
        },
        keepPreviousData: true,
    });

    return {
        noShows: noShowsQuery.isError ? undefined : noShowsQuery.data?.data,
        refetchNoShows: noShowsQuery.refetch,
        noShowsLoading: noShowsQuery.isLoading,
        forecast: forecastQuery.isError ? undefined : forecastQuery.data?.data,
        refetchForecast: forecastQuery.refetch,
        forecastLoading: forecastQuery.isLoading,
    };
}

export function useSwotWizard() {
    return useMutation({
        mutationFn: async (answers: Record<string, string>): Promise<AxiosResponse<SwotWizardResponse>> => {
            return await httpRequest.post(`/dashboard/predictive/swot-wizard`, { answers });
        },
    });
}
